import { CheckCircleFilled } from '@ant-design/icons';
import { templateAtVersion } from '@bmo/form-core';
import { Alert, Button, Popconfirm, Spin, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type WorkflowTemplate,
  type WorkflowVersion,
  type WorkflowVersionSummary,
  workflowsApi,
} from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { BmoModal } from '@/components/BmoModal';
import { PageHeader } from '@/components/PageHeader';
import { message } from '@/lib/message';
import { extractApiError } from '@/originator/statusUtils';
import { WorkflowReviewBody } from '@/originator/WorkflowReviewBody';
import { assigneeOf } from '@/utils/workflowHelpers';

const { Text } = Typography;

import { InlineWorkflowPreviewModal } from './InlineWorkflowPreviewModal';
import { RejectModal } from './RejectModal';
import { diffWorkflowSettings, diffWorkflowSteps } from './versionDiff';
import { VersionDiffPanel } from './VersionDiffPanel';

// localStorage key the ApproverDashboard uses to remember its active tab —
// setting it before we navigate lands the user on the Approved Workflows tab
// so the post-approve reminder's CTA jumps straight to the Publish toggle.
const APPROVER_TAB_KEY = 'bmo-inflow.approver.activeTab';

export function WorkflowReview() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { oid } = useRoles();
  const { id } = useParams<{ id: string }>();
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [latestVersion, setLatestVersion] = useState<WorkflowVersion | null>(null);
  // Immediate predecessor of the version being reviewed, sourced from the same
  // /versions listing. Powers the diff panel below; null when the workflow is
  // on its first version (nothing to diff against).
  const [previousVersion, setPreviousVersion] = useState<WorkflowVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  // AC2 on "Preview and reject all workflow changes at once": in-place preview
  // (no navigation) via a full-viewport modal. Opened from a button on the
  // version-diff panel header — see the button + modal render below.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [acting, setActing] = useState(false);
  // AC08 — publish reminder modal opens after a successful approve to nudge
  // the approver toward the Publish toggle (which lives one tab away and is
  // the actual gate that makes the workflow visible to advisors).
  const [publishReminderOpen, setPublishReminderOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const [t1, versions] = await Promise.all([
          workflowsApi.get(id),
          workflowsApi.listVersions(id),
        ]);
        if (cancelled) return;
        setTemplate(t1);
        // Pick the two rows to compare from metadata alone (status / number / fork
        // label), then hydrate just those — the versions list carries no step
        // snapshots, and the diff plus both preview panes need real step graphs.
        const pending = versions.find((v) => v.status === 'Pending');
        const primaryRow = pending ?? versions[0] ?? null;
        const previousRow = primaryRow ? resolveComparisonBase(primaryRow, versions) : null;
        const [primary, previous] = await Promise.all([
          primaryRow
            ? workflowsApi.getVersion(id, primaryRow.versionNumber)
            : Promise.resolve(null),
          previousRow
            ? workflowsApi.getVersion(id, previousRow.versionNumber)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setLatestVersion(primary);
        setPreviousVersion(previous);
      } catch (err) {
        message.error(extractApiError(err).message ?? t('approver.review.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  // Structural diff between the version being reviewed and its predecessor.
  // For a v1 first submission there IS no predecessor — pass prev=[] so the
  // walker enumerates every step + section + field as "added." That gives
  // the approver a full "here's the whole new form" tree for AC5 (feature
  // applies to both new form requests and change requests to existing forms).
  const versionDiff = useMemo(() => {
    if (!latestVersion) return null;
    return diffWorkflowSteps(previousVersion?.steps ?? [], latestVersion.steps ?? []);
  }, [latestVersion, previousVersion]);
  const settingChanges = useMemo(
    () => (latestVersion ? diffWorkflowSettings(previousVersion, latestVersion) : []),
    [latestVersion, previousVersion],
  );

  // Frozen-version template — swap `.steps` with the pending version's
  // snapshot so the preview modal shows exactly what advisors will see, not
  // the template's live state. Memoized so the reference is stable across
  // parent re-renders (setPreviewOpen, setPublishReminderOpen, etc.) —
  // otherwise FormShell's `template`-keyed prefill effects would re-fire on
  // every unrelated state change.
  const previewTemplate = useMemo<WorkflowTemplate | null>(() => {
    if (!template || !latestVersion) return null;
    return templateAtVersion(template, latestVersion);
  }, [template, latestVersion]);
  // Frozen predecessor template — powers the modal's "previous ↔ pending"
  // version toggle so the approver can flip between what advisors see today
  // and what they'll see after approval. Null on v1 first submissions; the
  // modal hides its version toggle when this is null.
  const previewPrevTemplate = useMemo<WorkflowTemplate | null>(() => {
    if (!template || !previousVersion) return null;
    return templateAtVersion(template, previousVersion);
  }, [template, previousVersion]);

  const approve = async () => {
    if (!id || !latestVersion) return;
    setActing(true);
    try {
      const updated = await workflowsApi.approve(id, latestVersion.versionNumber, i18n.language);
      setTemplate(updated);
      message.success(t('approver.review.approved'));
      // AC08 — open the branded publish-reminder modal (rendered below)
      // instead of `Modal.info`, which read as generic + off-brand.
      setPublishReminderOpen(true);
    } catch (err) {
      message.error(extractApiError(err).message ?? t('approver.review.approveFailed'));
    } finally {
      setActing(false);
    }
  };

  const reject = async (comment: string) => {
    if (!id || !latestVersion) return;
    try {
      const updated = await workflowsApi.reject(
        id,
        latestVersion.versionNumber,
        comment,
        i18n.language,
      );
      setTemplate(updated);
      message.success(t('approver.review.rejected'));
      setRejectOpen(false);
    } catch (err) {
      message.error(extractApiError(err).message ?? t('approver.review.rejectFailed'));
      throw err;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (!template) {
    return <Alert type="error" message={t('approver.review.notFound')} />;
  }

  const isFr = i18n.language.startsWith('fr');
  const name = isFr ? template.nameFr : template.nameEn;
  const description = isFr ? template.descriptionFr : template.descriptionEn;
  const canDecide = template.status === 'Pending' && latestVersion?.status === 'Pending';
  // AC02 (Approve a pending request): the user who created the workflow
  // cannot approve their own submission (segregation of duties). Compare
  // by oid — dev-login identities are single-role today but a real Entra
  // account can hold both Approver + Originator; without this the two-eyes
  // rule would rely purely on the BE guard. Reject stays available — an
  // originator rejecting-and-revising their own draft is fine.
  const isSelfSubmission = !!oid && oid === template.ownerOid;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title={template.nameEn || template.appName}
        onBack={() => navigate('/approver/dashboard')}
      />

      {/* Read-only Workflow Details + Steps — the reviewer's core view. Fed the
          live template's current steps; the per-step preview is pinned to the
          version under review so it shows exactly what advisors will experience. */}
      <WorkflowReviewBody
        isFr={isFr}
        name={name}
        description={description}
        assignee={assigneeOf(template, t)}
        platform={template.platform}
        versionLabel={latestVersion?.versionLabel}
        status={template.status}
        isActive={template.isActive}
        statusScope="template"
        rejectionComment={latestVersion?.rejectionComment}
        steps={template.steps ?? []}
        template={template}
        versionNumber={latestVersion?.versionNumber ?? null}
        previewBase="/approver/workflows"
        includedDealerCodes={template.includedDealerCodes}
        excludedDealerCodes={template.excludedDealerCodes}
        packageFilenameTemplate={template.packageFilenameTemplate}
        dateFormat={template.dateFormat}
        // Route the top-right "Preview" button to the in-place modal (with
        // the previous ↔ pending version toggle) rather than the standalone
        // /preview page — same modal used for AC2 on the reject-all-changes
        // story, keeps the reviewer in context of the diff panel below.
        onPreview={
          previewTemplate && latestVersion?.status === 'Pending'
            ? () => setPreviewOpen(true)
            : undefined
        }
      />

      {/* Version-over-version change summary — surfaces for every pending
          submission the reviewer is deciding on, including v1 first
          submissions (where previousVersion is null and every step is
          rendered as "added"). Already-decided rows skip this so the panel
          isn't noise. Preview is triggered from the "Preview" button
          rendered by <WorkflowReviewBody> above (via onPreview) rather than
          a duplicate button here — one preview affordance for the page. */}
      {versionDiff && latestVersion && latestVersion.status === 'Pending' && (
        <>
          <VersionDiffPanel
            isFr={isFr}
            nextLabel={latestVersion.versionLabel}
            prevLabel={previousVersion?.versionLabel ?? ''}
            firstSubmission={!previousVersion}
            diff={versionDiff}
            settingChanges={settingChanges}
          />
          {/* Mount-on-open — FormShell inside carries useState + useEffect
              chains keyed on `template`; keeping it out of the DOM until the
              approver actually clicks Preview avoids the eager prefill run on
              every review-page load, and unmount-on-close resets throwaway
              input state between opens (so a second open is a fresh preview,
              not a resumed one). */}
          {previewOpen && previewTemplate && (
            <InlineWorkflowPreviewModal
              open
              onCancel={() => setPreviewOpen(false)}
              template={previewTemplate}
              prevTemplate={previewPrevTemplate ?? undefined}
              nextLabel={latestVersion.versionLabel}
              prevLabel={previousVersion?.versionLabel}
            />
          )}
        </>
      )}

      {canDecide ? (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {isSelfSubmission && (
            <Alert
              type="warning"
              showIcon
              message={t('approver.review.selfApprovalBlocked', {
                defaultValue:
                  "You created this workflow, so you can't approve your own submission. Another approver has to review it. You can still reject it back to yourself for further changes.",
              })}
            />
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            {!isSelfSubmission && (
              <Popconfirm
                title={t('approver.review.approveConfirm')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={approve}
              >
                <Button type="primary" loading={acting}>
                  {t('approver.review.approve')}
                </Button>
              </Popconfirm>
            )}
            <Button danger onClick={() => setRejectOpen(true)} disabled={acting}>
              {t('approver.review.reject')}
            </Button>
            <RejectModal
              open={rejectOpen}
              onCancel={() => setRejectOpen(false)}
              onConfirm={reject}
            />
          </div>
        </div>
      ) : (
        <Alert
          type="info"
          showIcon
          message={t('approver.review.notDecidable', { status: template.status })}
        />
      )}

      {/* AC08 — publish reminder modal. Branded BmoModal shell (blue header,
          consistent close icon) + a big green success glyph so the tone reads
          as "you did the thing, one more click and you're done". Primary CTA
          hands the approver directly to the Approved Workflows tab where
          Publish lives; secondary keeps them on the current page. */}
      <BmoModal
        open={publishReminderOpen}
        onCancel={() => setPublishReminderOpen(false)}
        title={t('approver.review.publishReminderModalTitle', {
          defaultValue: 'Workflow approved',
        })}
        width={480}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              padding: '8px 0 20px 0',
            }}
          >
            <Button onClick={() => setPublishReminderOpen(false)}>
              {t('approver.review.publishReminderStay', { defaultValue: 'Stay here' })}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                try {
                  // useRememberedTab stores/reads the tab key as a raw string — no JSON.
                  localStorage.setItem(APPROVER_TAB_KEY, 'approved');
                } catch {
                  // localStorage quota / private mode — dashboard falls back to
                  // its last remembered value; the navigation itself still works.
                }
                setPublishReminderOpen(false);
                navigate('/approver/dashboard');
              }}
            >
              {t('approver.review.publishReminderCta', {
                defaultValue: 'Open Approved Workflows',
              })}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <CheckCircleFilled style={{ fontSize: 48, color: '#16a34a' }} />
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              color: '#111827',
              textAlign: 'center',
            }}
          >
            {t('approver.review.publishReminderTitle', {
              defaultValue: 'One more step to publish',
            })}
          </h2>
          <Text style={{ fontSize: 14, color: '#4b5563', textAlign: 'center', lineHeight: 1.6 }}>
            {t('approver.review.publishReminderBody', {
              defaultValue:
                "Advisors won't see this workflow until you flip the Publish toggle on the Approved Workflows tab.",
            })}
          </Text>
        </div>
      </BmoModal>
    </div>
  );
}

/**
 * Pick the version that the pending row should be diffed against. Mirrors what
 * the originator started editing from:
 *
 *   1. If the originator explicitly forked (e.g. v3 forked from v1 skipping v2),
 *      `forkedFromLabel` carries the source's `versionLabel` — match by label
 *      so the diff base is the same row the originator branched off.
 *   2. Otherwise the domain rebases the new draft onto the most recent Approved
 *      row's frozen snapshot (Draft/Rejected rows can't be fork sources per
 *      `StartNewVersion`), so fall back to that.
 *   3. If there's no prior Approved row (first submission after only rejected
 *      drafts) fall back to the immediate predecessor by number so the panel
 *      still shows something rather than silently disappearing.
 */
function resolveComparisonBase(
  primary: WorkflowVersionSummary,
  versions: readonly WorkflowVersionSummary[],
): WorkflowVersionSummary | null {
  if (primary.forkedFromLabel) {
    const forked = versions.find((v) => v.versionLabel === primary.forkedFromLabel);
    if (forked) return forked;
  }
  const priorApproved = versions
    .filter((v) => v.versionNumber < primary.versionNumber && v.status === 'Approved')
    .sort((a, b) => b.versionNumber - a.versionNumber)[0];
  if (priorApproved) return priorApproved;
  return (
    versions
      .filter((v) => v.versionNumber < primary.versionNumber)
      .sort((a, b) => b.versionNumber - a.versionNumber)[0] ?? null
  );
}
